<template>
  <div class="app-shell">
    <Toast />
    <!-- Dedicated group for message push notifications: clicking the toast
         navigates to the conversation instead of just dismissing it. -->
    <Toast group="message-nav">
      <template #message="slotProps">
        <span class="p-toast-message-icon pi pi-comments"></span>
        <div class="p-toast-message-text message-nav-toast" @click="onMessageToastClick(slotProps.message)">
          <span class="p-toast-summary">{{ slotProps.message.summary }}</span>
          <div class="p-toast-detail">{{ slotProps.message.detail }}</div>
        </div>
      </template>
    </Toast>
    <ConfirmPopup
      class="fixed w-[400px] top-[200px] left-0 right-0 mx-auto before:hidden after:hidden shadow-none rounded-[10px] !z-[1111]">
    </ConfirmPopup>
    <CreatePostModal v-if="userdata" v-model:isShow="isShow" is-close-active :main-title="'Compose'"
      :user-data="userdata" />
    <CreatePostGroupModal v-if="userdata" v-model:isShow="isShowOrgModal" is-close-active :main-title="'Compose'"
      :user-data="userdata" />

    <!-- Nav sidebar (far left, collapsible) -->
    <NavSidebar @toggle="onSidebarToggle" :mobileOpen="mobileLeftOpen" @close="activeMobile = ''" />

    <!-- Main area: header + content row -->
    <div class="app-shell__main" :class="{ 'app-shell__main--collapsed': sidebarCollapsed }">
      <!-- activeMobile prop lets NavHeader sync its local panels; @set-mobile toggles one slot at a time -->
      <NavHeader
        :activeMobile="activeMobile"
        @set-mobile="setMobile"
      />

      <!-- Content row: feed + widget sidebar -->
      <div class="app-shell__content">
        <!-- Main feed / router view -->
        <!-- KeepAlive preserves HomeView's scroll position across post-detail navigation,
             matching how Facebook/Instagram handle feed scroll restoration. -->
        <div class="app-shell__feed">
          <div class="stable-router-view">
            <RouterView v-slot="{ Component }">
              <KeepAlive :include="['HomeView']">
                <component :is="Component" />
              </KeepAlive>
            </RouterView>
          </div>
        </div>

        <!-- Desktop: Rightbar as normal sidebar column (≥900px) -->
        <div class="app-shell__widgets" v-if="showWidgetSidebar && !hideRightbar">
          <NavRightbar :mobileOpen="false" @close="() => {}" />
        </div>
        <!-- Mobile: Rightbar as right-side drawer (rendered outside the widget column) -->
        <NavRightbar v-if="!showWidgetSidebar && !hideRightbar" :mobileOpen="mobileRightOpen" @close="activeMobile = ''" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// console.log("Top-level log in NavSidebar.vue")
import { computed, onBeforeUnmount, onMounted, ref, defineAsyncComponent } from 'vue';
import { useRoute } from 'vue-router';
import ConfirmPopup from 'primevue/confirmpopup';
import Toast from 'primevue/toast';
import { useStore } from 'vuex';
import NavHeader from '@/components/NavHeader/NavHeader.vue';
import NavSidebar from '@/components/NavSidebar/NavSidebar.vue';
import NavRightbar from '@/components/NavRightbar/NavRightbar.vue';
import { debounce } from '@/helpers/debounceHelper.js';

const CreatePostModal = defineAsyncComponent(() => import('../components/modals/CreatePost.vue'));
const CreatePostGroupModal = defineAsyncComponent(() => import('../components/modals/GroupModals/createPostGroup.vue'));

const store = useStore();
const route = useRoute();
const sidebarCollapsed = ref(false);
const showWidgetSidebar = ref(true);
const hideRightbar = computed(() => Boolean(route.meta?.hideRightbar));

// Single mobile panel state — only one thing can be open at a time.
// Values: '' | 'left' | 'right' | 'analytics' | 'search'
const activeMobile = ref('');
const mobileLeftOpen = computed(() => activeMobile.value === 'left');
const mobileRightOpen = computed(() => activeMobile.value === 'right');
const setMobile = (val: string) => { activeMobile.value = activeMobile.value === val ? '' : val; };

const isShow = computed(() => store.getters.getShowModalStatus);
const isShowOrgModal = computed(() => store.getters.getOrgShowModalStatus);
const userdata = computed(() => store.getters.getUser);

const onSidebarToggle = (collapsed) => {
  sidebarCollapsed.value = collapsed;
};

const onMessageToastClick = (message) => {
  message?.data?.onClick?.();
};

const checkLayout = () => {
  const width = window.innerWidth;
  showWidgetSidebar.value = width > 900;
};

const debouncedCheck = debounce(checkLayout, 100);
const onResize = () => debouncedCheck();

onMounted(() => {
  checkLayout();
  window.addEventListener('resize', onResize);

  const preloadModals = () => {
    import('../components/modals/CreatePost.vue');
    import('../components/modals/GroupModals/createPostGroup.vue');
  };
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(preloadModals, { timeout: 2000 });
  } else {
    setTimeout(preloadModals, 1500);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
});
</script>

<style lang="scss">
/* ─── App shell: flex row, full viewport ─── */
.app-shell {
  display: flex;
  flex-direction: row;
  height: 100vh;
  overflow: hidden;
  background: var(--color-app-background-gradient, linear-gradient(135deg, var(--color-background-primary) 0%, var(--color-background-secondary) 100%));
  padding: 0 0px 0 0; /* 20px right padding; left padding handled by sidebar */

  /* ─── Main area next to sidebar ─── */
  &__main {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
    transition: margin-left 0.28s cubic-bezier(0.4, 0, 0.2, 1);

    /* extra left padding removed — sidebar handles its own width */
  }

  /* ─── Content row: widgets + feed ─── */
  &__content {
    display: flex;
    flex-direction: row;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    gap: 0;
  }

  /* ─── Widget / merged sidebar ─── */
  &__widgets {
    // width: 280px;
    // min-width: 260px;
    // max-width: 300px;
    // width: 340px;
    // min-width: 260px;
    // max-width: 340px;
    width: 280px;
    min-width: 240px;
    max-width: 280px;
    flex-shrink: 0;
    height: 100%;
    overflow-y: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
    // border-right: 1px solid var(--color-header-border-light-v2, rgba(0,0,0,0.08));
  }

  /* ─── Main feed ─── */
  &__feed {
    flex: 1;
    min-width: 0;
    height: 100%;
    overflow-y: hidden;
    overflow-x: hidden;
    position: relative;
    z-index: 20;
    padding: 0px 15px;
  }

  /* ─── Responsive ─── */
  @media (max-width: 900px) {
    padding: 0;

    &__widgets {
      display: none;
    }
  }

  @media (max-width: 639px) {
    flex-direction: column;

    &__main {
      flex: 1;
    }
  }
}

/* Router view stable layout */
.stable-router-view {
  width: 100%;
  height: 100%;
  contain: layout;
  isolation: isolate;
  position: relative;
}

/* Toast positioning */
.message-nav-toast {
  cursor: pointer;
}

.p-toast {
  position: fixed !important;
  top: 30px !important;
  left: 0 !important;
  right: 0 !important;
  margin-left: auto !important;
  margin-right: auto !important;
  max-width: 100%;
  z-index: 4000 !important;
}
</style>
